import React, { useEffect, useState } from 'react';
import {
  Container,
  ThemeTable,
  ThemeInput,
  ThemeSelect,
  DogByIDModal,
  DogsModal,
  UserDetailModal,
  VaccinationStatusBadge,
  EditDogModal,
} from '../../components/components';
import {
  useLazyGetDogByIDQuery,
  useSearchDogsQuery,
} from '../../services/dogsApi';
import { useLazyGetUserByIdQuery } from '../../services/users';
import { PiDogFill } from 'react-icons/pi';
import { FaUser, FaEdit } from 'react-icons/fa';
import { happy_dog } from '../../assets/images/images';

// CSS animations for vaccination status highlighting
if (typeof document !== 'undefined') {
  const styleId = 'vaccination-status-styles';
  if (!document.getElementById(styleId)) {
    const styles = `
      @keyframes slideInGlowRed {
        0% {
          background-color: rgba(239, 68, 68, 0.4);
          transform: scale(1.02);
          box-shadow: 0 0 15px rgba(239, 68, 68, 0.5);
        }
        50% {
          background-color: rgba(239, 68, 68, 0.2);
          transform: scale(1.01);
          box-shadow: 0 0 10px rgba(239, 68, 68, 0.3);
        }
        100% {
          background-color: rgba(239, 68, 68, 0.1);
          transform: scale(1);
          box-shadow: 0 0 5px rgba(239, 68, 68, 0.2);
        }
      }

      @keyframes slideInGlowOrange {
        0% {
          background-color: rgba(249, 115, 22, 0.4);
          transform: scale(1.02);
          box-shadow: 0 0 15px rgba(249, 115, 22, 0.5);
        }
        50% {
          background-color: rgba(249, 115, 22, 0.2);
          transform: scale(1.01);
          box-shadow: 0 0 10px rgba(249, 115, 22, 0.3);
        }
        100% {
          background-color: rgba(249, 115, 22, 0.1);
          transform: scale(1);
          box-shadow: 0 0 5px rgba(249, 115, 22, 0.2);
        }
      }

      @keyframes slideInGlowGray {
        0% {
          background-color: rgba(107, 114, 128, 0.4);
          transform: scale(1.02);
          box-shadow: 0 0 15px rgba(107, 114, 128, 0.5);
        }
        50% {
          background-color: rgba(107, 114, 128, 0.2);
          transform: scale(1.01);
          box-shadow: 0 0 10px rgba(107, 114, 128, 0.3);
        }
        100% {
          background-color: rgba(107, 114, 128, 0.1);
          transform: scale(1);
          box-shadow: 0 0 5px rgba(107, 114, 128, 0.2);
        }
      }

      .expired-vaccination-row {
        animation: slideInGlowRed 2s ease-out;
        background-color: rgba(239, 68, 68, 0.1) !important;
        border-left: 4px solid #ef4444 !important;
        transition: all 0.3s ease;
      }

      .expired-vaccination-row:hover {
        background-color: rgba(239, 68, 68, 0.15) !important;
      }

      .expiring-soon-vaccination-row {
        animation: slideInGlowOrange 2s ease-out;
        background-color: rgba(249, 115, 22, 0.1) !important;
        border-left: 4px solid #f97316 !important;
        transition: all 0.3s ease;
      }

      .expiring-soon-vaccination-row:hover {
        background-color: rgba(249, 115, 22, 0.15) !important;
      }

      .no-records-vaccination-row {
        animation: slideInGlowGray 2s ease-out;
        background-color: rgba(107, 114, 128, 0.1) !important;
        border-left: 4px solid #6b7280 !important;
        transition: all 0.3s ease;
      }

      .no-records-vaccination-row:hover {
        background-color: rgba(107, 114, 128, 0.15) !important;
      }

      .deleted-dog-row {
        background-color: rgba(156, 163, 175, 0.1) !important;
        opacity: 0.7;
        border-left: 4px solid #9ca3af !important;
      }

      .deleted-dog-row:hover {
        background-color: rgba(156, 163, 175, 0.15) !important;
      }
    `;
    const styleSheet = document.createElement('style');
    styleSheet.id = styleId;
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
  }
}

const Dogs = () => {
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [resultPerPage, setResultPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [isDogModalOpen, setIsDogModalOpen] = useState(false);
  const [isOwnerModalOpen, setIsOwnerModalOpen] = useState(false);
  const [isUserDetailModalOpen, setIsUserDetailModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editModalInitialTab, setEditModalInitialTab] = useState('1');
  const [vaccinationFilter, setVaccinationFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');

  // Debounce search input - wait 400ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      if (searchInput !== search) {
        setCurrentPage(1);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading } = useSearchDogsQuery({
    name: search,
    currentPage,
    resultPerPage,
    vaccinationFilter,
    activeFilter,
  });

  const [getDogByID, { isLoading: fetchingDog }] = useLazyGetDogByIDQuery();
  const [getUserById, { isLoading: fetchingOwner }] = useLazyGetUserByIdQuery();
  const [dogData, setDogData] = useState(null);
  const [ownerData, setOwnerData] = useState(null);
  const [userDetailData, setUserDetailData] = useState(null);

  useEffect(() => {
    // Backend may return several shapes:
    // 1) { data: [ { dogs: [...], total } ] }
    // 2) { data: { items: [...], total, page, perPage } }
    // 3) { data: [...] }
    const totalFromShape =
      data?.data?.[0]?.total ??
      data?.data?.total ??
      (Array.isArray(data?.data) ? data.data.length : undefined) ??
      0;
    setTotal(totalFromShape);
  }, [data, isLoading]);

  const onOpenDog = async id => {
    setDogData(null);
    const res = await getDogByID(id);
    setDogData(res?.data?.data || null);
    setIsDogModalOpen(true);
  };

  const onOpenOwner = async ownerId => {
    setOwnerData(null);
    const res = await getUserById({ id: ownerId });
    setOwnerData(res?.data?.data || null);
    setIsOwnerModalOpen(true);
  };

  const onOpenOwnerProfile = async ownerId => {
    const res = await getUserById({ id: ownerId });
    setUserDetailData(res?.data?.data || null);
    setIsUserDetailModalOpen(true);
  };

  const handleViewUserDetails = data => {
    setUserDetailData(data);
    setIsOwnerModalOpen(false);
    setIsUserDetailModalOpen(true);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setCurrentPage(1);
  };

  const onOpenEditModal = async (id, tab = '1') => {
    setDogData(null);
    setEditModalInitialTab(tab);
    const res = await getDogByID(id);
    setDogData(res?.data?.data || null);
    setIsEditModalOpen(true);
  };

  const onOpenVaccinationsEdit = async id => {
    await onOpenEditModal(id, '4'); // '4' is the Vaccinations tab
  };

  const columns = [
    {
      title: 'Dog',
      dataIndex: 'dog_name',
      key: 'dog_name',
      render: (text, record) => (
        // Fixed-width block centred under the header, so avatars line up in a
        // column instead of shifting with each name's length.
        <div className="flex justify-center">
          <div
            className="group flex items-center gap-4 w-64 cursor-pointer"
            onClick={() => onOpenDog(record._id)}
          >
            <img
              src={record.avatar || happy_dog}
              className="w-16 h-16 shrink-0 rounded-full object-cover"
              alt="avatar"
            />
            <div className="min-w-0">
              <p className="break-words font-semibold text-lg text-black group-hover:underline">
                {text}
              </p>
              {record.breed && (
                <p className="break-words text-sm text-gray-500">
                  {record.breed}
                </p>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
      render: owner =>
        owner ? (
          <p
            className="text-center font-medium text-primary cursor-pointer hover:underline"
            onClick={() => onOpenOwnerProfile(owner._id)}
          >
            {owner.full_name || owner.username || owner.email}
          </p>
        ) : (
          <p className="text-center text-gray-400">—</p>
        ),
    },
    {
      title: 'Vaccination Status',
      dataIndex: 'vaccination_status',
      key: 'vaccination_status',
      render: (status, record) => (
        <div className="flex justify-center">
          <VaccinationStatusBadge
            hasExpired={status?.hasExpired}
            hasExpiringSoon={status?.hasExpiringSoon}
            hasNoRecords={status?.hasNoRecords}
            expiredCount={status?.expiredCount}
            expiringSoonCount={status?.expiringSoonCount}
            onClick={() => onOpenVaccinationsEdit(record._id)}
          />
        </div>
      ),
    },
    {
      title: 'Actions',
      dataIndex: '_id',
      key: 'actions',
      render: (id, record) => {
        const isDeleted = record.active === false;
        return (
          <div className="flex flex-col items-center gap-2">
            <button
              onClick={() => onOpenDog(id)}
              className="text-primary hover:text-primary-dark text-sm font-medium flex items-center gap-1"
            >
              <PiDogFill size={16} />
              View Dog
            </button>
            {!isDeleted && (
              <button
                onClick={() => onOpenEditModal(id)}
                className="text-green-600 hover:text-green-800 text-sm font-medium flex items-center gap-1"
              >
                <FaEdit size={14} />
                Edit Dog
              </button>
            )}
            {isDeleted && (
              <span className="text-red-600 text-xs font-medium px-2 py-1 bg-red-50 rounded">
                Deleted
              </span>
            )}
            {record.owner_id && (
              <button
                onClick={() => onOpenOwner(record.owner_id)}
                className="text-primary hover:text-primary-dark text-sm font-medium flex items-center gap-1"
              >
                <FaUser size={14} />
                View Owner's Dogs
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Container
      selected={'8'}
      header={
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <div className="bg-primary h-9 w-9 rounded-full flex justify-center items-center">
              <PiDogFill className="text-white text-lg" />
            </div>
            <p className="ml-2 font-semibold text-2xl">Dogs</p>
          </div>

          <div className="flex items-center gap-4 w-9/12">
            <ThemeSelect
              value={
                activeFilter === 'deleted'
                  ? 'deleted'
                  : vaccinationFilter === 'expired'
                    ? 'expired'
                    : 'all'
              }
              onChange={value => {
                setCurrentPage(1);
                if (value === 'deleted') {
                  setActiveFilter('deleted');
                  setVaccinationFilter('all');
                } else if (value === 'expired') {
                  setActiveFilter('all');
                  setVaccinationFilter('expired');
                } else {
                  setActiveFilter('all');
                  setVaccinationFilter('all');
                }
              }}
              placeholder="Filter dogs"
              className="w-48"
              options={[
                { label: 'All Dogs', value: 'all' },
                { label: 'Expired Vaccinations Only', value: 'expired' },
                { label: 'Deleted Dogs Only', value: 'deleted' },
              ]}
            />
            <ThemeInput
              placeholder={'Search dogs by name'}
              className={'flex-1'}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <button
                onClick={handleClearSearch}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      }
    >
      <ThemeTable
        columns={columns}
        // Normalize backend shapes into an array for the table
        data={
          // prefer older shape: data[0].dogs
          Array.isArray(data?.data?.[0]?.dogs)
            ? data.data[0].dogs
            : // new shape: data.items
              Array.isArray(data?.data?.items)
              ? data.data.items
              : // data.data might be an array already
                Array.isArray(data?.data)
                ? data.data
                : // fallback empty
                  []
        }
        pagination={{ total, setCurrentPage, setResultPerPage }}
        loader={isLoading}
        entityName="dogs"
        emptyText={
          search
            ? `No dogs match "${search}".`
            : vaccinationFilter !== 'all' || activeFilter !== 'all'
              ? 'No dogs match the selected filters. Try resetting them to All.'
              : 'No dogs yet. They appear here once an owner adds one in the app.'
        }
        onRow={record => ({
          className:
            record.active === false
              ? 'deleted-dog-row'
              : record.vaccination_status?.hasExpired
                ? 'expired-vaccination-row'
                : record.vaccination_status?.hasExpiringSoon
                  ? 'expiring-soon-vaccination-row'
                  : record.vaccination_status?.hasNoRecords
                    ? 'no-records-vaccination-row'
                    : '',
        })}
      />

      <DogByIDModal
        isOpen={isDogModalOpen}
        onClose={() => setIsDogModalOpen(false)}
        data={dogData}
        isLoading={fetchingDog}
        onEditDog={() => {
          setIsDogModalOpen(false);
          setIsEditModalOpen(true);
        }}
        onViewOwner={onOpenOwner}
        onDeleteSuccess={() => {
          setIsDogModalOpen(false);
          // Data will refresh automatically via RTK Query cache invalidation
        }}
        zIndex={1010}
      />

      <DogsModal
        isOpen={isOwnerModalOpen}
        onClose={() => setIsOwnerModalOpen(false)}
        data={ownerData}
        isLoading={fetchingOwner}
        onViewUserDetails={handleViewUserDetails}
        onViewDog={onOpenDog}
        zIndex={1020}
      />

      <UserDetailModal
        isOpen={isUserDetailModalOpen}
        onClose={() => setIsUserDetailModalOpen(false)}
        data={userDetailData}
      />

      <EditDogModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        data={dogData}
        initialTab={editModalInitialTab}
        onSuccess={() => {
          // Refresh the list after successful operations
        }}
        zIndex={1010}
      />
    </Container>
  );
};

export default Dogs;
